import { useEffect, useState } from "react";
import { useFranklinContext } from "../../context/franklin-context";
import type { Article, Category } from "../../types";

export function KnowledgeBase() {
  const { apiEndpoint, orgId, sdkKey } = useFranklinContext();
  const [articles, setArticles] = useState<Article[]>([]);
  const [_categories, setCategories] = useState<Category[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchKnowledgeBase();
  }, []);

  const fetchKnowledgeBase = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${apiEndpoint}/knowledge-base`, {
        headers: {
          "X-Franklin-Org-Id": orgId,
          "X-Franklin-SDK-Key": sdkKey,
        },
      });
      const data = await response.json();
      setArticles(data.articles || []);
      setCategories(data.categories || []);
    } catch (error) {
      console.error("Failed to fetch knowledge base:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredArticles = articles.filter((article) =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-gray-500 text-sm">Loading knowledge base...</div>
      </div>
    );
  }

  if (selectedArticle) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-[var(--franklin-border)] border-b px-6 py-4">
          <button
            className="mb-2 text-blue-600 text-sm hover:text-blue-700"
            onClick={() => setSelectedArticle(null)}
            type="button"
          >
            ← Back to articles
          </button>
          <h2 className="font-semibold text-[var(--franklin-foreground)] text-xl">
            {selectedArticle.title}
          </h2>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div
            className="prose prose-sm max-w-none text-gray-700"
            /* biome-ignore lint/security/noDangerouslySetInnerHtml: content sanitized upstream */
            dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-[var(--franklin-border)] border-b px-6 py-4">
        <h2 className="mb-3 font-semibold text-[var(--franklin-foreground)] text-lg">
          Knowledge Base
        </h2>
        <input
          className="w-full rounded-lg border border-[var(--franklin-border)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Search articles..."
          type="text"
          value={searchQuery}
        />
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {filteredArticles.length === 0 ? (
          <div className="py-8 text-center text-gray-500 text-sm">
            {searchQuery ? "No articles found" : "No articles available"}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredArticles.map((article) => (
              <button
                className="w-full rounded-lg border border-[var(--franklin-border)] p-4 text-left transition-colors hover:bg-gray-50"
                key={article.id}
                onClick={() => setSelectedArticle(article)}
                type="button"
              >
                <h3 className="mb-1 font-medium text-[var(--franklin-foreground)] text-sm">
                  {article.title}
                </h3>
                {article.excerpt && (
                  <p className="line-clamp-2 text-[var(--franklin-muted)] text-xs">
                    {article.excerpt}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
